import React, { useEffect, useState, useRef, useMemo } from 'react'
import type { HotelAvailabilityParams } from '~/hooks/type'
import type { RoomAvailabilitySearch, RoomPriceGroup, MappedPrices } from '~/types/hotel'
import { useSearchParams } from 'next/navigation'
import { useHotelAvailabilitySearchStore } from '~/stores/hotel-availability-search'

const API_BASE_URL = process.env.NEXT_PUBLIC_INV_URL

interface ApiResponse {
  isSuccess: boolean
  data: RoomAvailabilitySearch[]
}

const buildUrl = (params: HotelAvailabilityParams) => {
  const queryParams = new URLSearchParams()
  queryParams.append('hotelId', params.hotelId)
  queryParams.append('fromDate', params.fromDate)
  queryParams.append('toDate', params.toDate)
  queryParams.append('adults', params.adults.toString())
  queryParams.append('children', params.children.toString())
  return `${API_BASE_URL}/cm/inventory/hotel-available/search?${queryParams}`
}

const fetcher = async (url: string): Promise<RoomAvailabilitySearch[]> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Network response was not ok')
  }
  const result = (await response.json()) as ApiResponse
  return result.data
}

const groupRoomPrices = (data: RoomAvailabilitySearch[]): RoomPriceGroup[] => {
  if (!data || data?.length <= 0) {
    return []
  }
  const groups: { [key: string]: RoomPriceGroup } = {}

  data.forEach((room) => {
    const date = new Date(room.date)
    const isDayType = date.getDay() === 0 || date.getDay() === 6 ? 'weekend' : 'weekday'
    const key = `${room.roomTypeCode}-${isDayType}`

    if (!groups[key]) {
      groups[key] = {
        roomTypeCode: room.roomTypeCode,
        roomTypeId: room.roomTypeId,
        dayType: isDayType,
        lowestRate: room.rate,
        dates: [room.date],
        availableRooms: room.availableRooms,
        currency: room.currency,
        packagePlanCode: room.packagePlanCode,
        refundable: room.refundable,
      }
    } else {
      if (room.rate < groups[key].lowestRate) {
        groups[key].lowestRate = room.rate
      }
      groups[key].dates.push(room.date)
      groups[key].availableRooms = Math.min(groups[key].availableRooms, room.availableRooms)
    }
  })

  return Object.values(groups)
}

export const mappedPrices = (
  data: RoomAvailabilitySearch[],
  fromDate: string,
  toDate: string
): MappedPrices => {
  const obj: MappedPrices = {}

  const checkInTime = new Date(fromDate).getTime()
  const checkOutTime = new Date(toDate).getTime()
  const isSameDay = fromDate === toDate
  if (!data || data?.length <= 0) {
    return obj
  }
  data.forEach((room) => {
    const { roomTypeCode, date, rate } = room
    const currentDate = new Date(date).getTime()

    if (
      (isSameDay && date === fromDate) ||
      (!isSameDay && currentDate >= checkInTime && currentDate < checkOutTime)
    ) {
      if (!obj[roomTypeCode]) {
        obj[roomTypeCode] = {
          totalPrice: 0,
          list: [],
        }
      }
      obj[roomTypeCode].list.push({ date, rate })
    }
  })

  Object.keys(obj).forEach((roomTypeCode) => {
    obj[roomTypeCode].totalPrice = obj[roomTypeCode].list.reduce((sum, item) => sum + item.rate, 0)
    obj[roomTypeCode].list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  })

  return obj
}

export function useHotelAvailabilitySearch(params?: HotelAvailabilityParams) {
  const [availability, setAvailabilityState] = useState<RoomAvailabilitySearch[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const prevParamsRef = useRef<HotelAvailabilityParams | null>(null)
  const nextMonthDataRef = useRef<RoomAvailabilitySearch[]>([])

  const {
    setAvailability,
    setGroupedPrices,
    setSearchParams,
    setLoading,
    setError: setStoreError,
    setMappedPrices,
  } = useHotelAvailabilitySearchStore()

  const searchParams = useSearchParams()

  const fetchData = async (fetchParams: HotelAvailabilityParams) => {
    try {
      const data = await fetcher(buildUrl(fetchParams))
      return data
    } catch (err) {
      throw err
    }
  }

  const mainFetch = async () => {
    if (!params) return

    // Check if params have actually changed
    if (prevParamsRef.current && JSON.stringify(prevParamsRef.current) === JSON.stringify(params)) {
      return
    }

    setIsLoading(true)
    setError(null)
    setLoading(true)
    setStoreError(false)

    try {
      const data = await fetchData(params)
      setAvailabilityState(data)
      prevParamsRef.current = params
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('An error occurred')
      setError(errorObj)
      setStoreError(true)
    } finally {
      setIsLoading(false)
      setLoading(false)
    }
  }

  // Update store when data changes
  useEffect(() => {
    console.log('availability', availability)
    if (!availability || !availability?.length || !params?.fromDate || !params?.toDate) return

    const urlCheckIn = searchParams.get('checkInDate')
    const urlCheckOut = searchParams.get('checkOutDate')

    if (
      urlCheckIn &&
      urlCheckOut &&
      urlCheckIn === params.fromDate &&
      urlCheckOut === params.toDate
    ) {
      setAvailability(availability)
      const _groupedPrices = groupRoomPrices(availability)
      if (_groupedPrices.length <= 0) {
        return
      }
      setGroupedPrices(_groupedPrices)
      const _mappedPrices = mappedPrices(availability, params.fromDate, params.toDate)
      if (!_mappedPrices) {
        return
      }
      setMappedPrices(_mappedPrices)

      setSearchParams((prev) => {
        const isSame =
          prev?.hotelId === params?.hotelId &&
          prev?.fromDate === params?.fromDate &&
          prev?.toDate === params?.toDate

        return isSame ? prev : params
      })
    }
  }, [availability, searchParams])

  // Main data fetching effect
  useEffect(() => {
    if (!params?.hotelId || !params?.fromDate || !params?.toDate) return
    mainFetch()
  }, [params?.hotelId, params?.fromDate, params?.toDate, params?.adults, params?.children])

  const groupedPrices = useMemo(
    () => (availability ? groupRoomPrices(availability) : []),
    [availability]
  )

  if (!params?.hotelId) {
    return {
      availability: [],
      groupedPrices: [],
      isLoading: false,
      isError: false,
      refetch: () => {},
    }
  }

  return {
    availability: availability || [],
    groupedPrices,
    isLoading,
    isError: !!error,
    refetch: mainFetch,
  }
}
